import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const AGBPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>AGB - Dein Gutachten Direkt</title>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/agb" />
      </Helmet>
      
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Allgemeine Geschäftsbedingungen (AGB)</h1>
          <p className="text-lg text-gray-600 mb-12">für Website, Leistungen und Partner-Portal</p>
          
          {/* 1. Geltungsbereich */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Geltungsbereich</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700">
                Diese AGB gelten für die Nutzung der Website sowie für die Zusammenarbeit mit Partnern über das Partner-Portal der Dein-Gutachten-Direkt GbR.
              </p>
            </div>
          </section>
          
          {/* 2. Leistungen */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Leistungen</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700 mb-2">
                Wir stellen Informationen rund um Unfallgutachten bereit und bieten Partnern die Möglichkeit, Fälle digital zu übermitteln.
              </p>
              <p className="text-gray-700">
                Die Bearbeitung eines Falls erfolgt auf Basis der vom Partner bereitgestellten Angaben, Fotos und Dokumente.
              </p>
            </div>
          </section>
          
          {/* 3. Partner-Portal Zugang */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Partner-Portal Zugang</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700 mb-2">
                Partner erhalten nach Registrierung und erfolgreicher Freischaltung einen Zugang zum Portal.
              </p>
              <p className="text-gray-700">
                Zugangsdaten sind vertraulich zu behandeln und dürfen nicht an Dritte weitergegeben werden.
              </p>
            </div>
          </section>
          
          {/* 4. Pflichten der Partner */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Pflichten der Partner</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700 mb-4">Partner verpflichten sich insbesondere:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Angaben wahrheitsgemäß und vollständig zu machen</li>
                <li>keine relevanten Informationen zurückzuhalten (z. B. Vorschäden, bekannte Mängel, besondere Umstände)</li>
                <li>nur passende und aussagekräftige Fotos hochzuladen</li>
                <li>keine fremden Rechte zu verletzen (z. B. Datenschutz, Urheberrechte)</li>
              </ul>
            </div>
          </section>
          
          {/* 5. Unvollständige Angaben */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Umgang mit unvollständigen oder falschen Angaben</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700 mb-2">
                Wenn Angaben oder Unterlagen unvollständig oder widersprüchlich sind, kann dies zu Rückfragen, Verzögerungen oder einer Ablehnung der Bearbeitung führen.
              </p>
              <p className="text-gray-700">
                Wir behalten uns vor, Fälle abzulehnen, wenn wesentliche Informationen fehlen oder der Verdacht auf falsche Angaben besteht.
              </p>
            </div>
          </section>
          
          {/* 6. Vergütung */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Vergütung / Abonnement (Partner)</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700 mb-2">
                Für den Zugang zum Partner-Portal fällt ein monatliches Abonnement an (Starter: 29,99 €, Pro: 49,99 €, Premium: 79,99 € inkl. MwSt.).
              </p>
              <p className="text-gray-700">
                Die Abrechnung erfolgt über den jeweils angebotenen Zahlungsanbieter.
              </p>
            </div>
          </section>
          
          {/* 7. Provision */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Provision (Partner)</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700 mb-4">
                Partner erhalten eine Vermittlungsprovision auf Basis des tatsächlich regulierten Netto-Grundhonorars.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Provisionsstufen:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>30% Standard-Provision</li>
                <li>40% ab 10 übermittelten Fällen pro Monat</li>
                <li>50% ab 20 übermittelten Fällen pro Monat</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Eine höhere Stufe wird erst gewährt, wenn die jeweilige Fallzahl mindestens 3 Monate in Folge erreicht wird.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Kürzungen durch Versicherungen:</h3>
              <p className="text-gray-700 mb-4">
                Kürzungen werden vom Netto-Grundhonorar abgezogen. Die Provision berechnet sich aus dem reduzierten Betrag.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Auszahlung:</h3>
              <p className="text-gray-700">
                Die Provision wird nach erfolgreicher Schadenregulierung und Zahlungseingang ausgezahlt, in der Regel innerhalb von 7 Tagen.
              </p>
            </div>
          </section>
          
          {/* 8. Keine Rechtsberatung */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Keine Rechtsberatung</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700">
                Wir bieten keine Rechtsberatung an. Inhalte auf der Website und im Portal dienen der Information.
              </p>
            </div>
          </section>
          
          {/* 9. Haftung */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Haftung</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700 mb-2">
                Wir haften nicht für Schäden, die durch falsche oder unvollständige Angaben entstehen, die durch Partner oder Dritte übermittelt wurden.
              </p>
              <p className="text-gray-700">
                Die Haftung ist auf Vorsatz und grobe Fahrlässigkeit beschränkt, soweit gesetzlich zulässig.
              </p>
            </div>
          </section>
          
          {/* 10. Verfügbarkeit */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Verfügbarkeit</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700">
                Wir bemühen uns um eine stabile Verfügbarkeit des Portals. Wartung, Updates oder technische Störungen können jedoch zeitweise Einschränkungen verursachen.
              </p>
            </div>
          </section>
          
          {/* 11. Kündigung */}
          <section className="mb-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Kündigung</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700">
                Das Abonnement kann nach den im Checkout/Portal angegebenen Bedingungen gekündigt werden. Bereits gezahlte Beträge werden nicht anteilig erstattet, sofern gesetzlich nichts anderes vorgeschrieben ist.
              </p>
            </div>
          </section>
          
          {/* 12. Schlussbestimmungen */}
          <section className="pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Schlussbestimmungen</h2>
            <div className="prose prose-lg">
              <p className="text-gray-700 mb-2">Es gilt deutsches Recht.</p>
              <p className="text-gray-700">
                Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit der übrigen Regelungen unberührt.
              </p>
            </div>
          </section>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AGBPage;
